/**
 * Como o nome do fornecedor aparece quando ele tem PARENTES (mesma raiz de CNPJ) — 17/09/2026.
 *
 * O LUGAR (cidade do cadastro, ou "CNPJ final 0003-43" sem cidade) vem pronto do servidor (`lugarDoFornecedor`);
 * aqui só se decide a FRASE. Decisão do Matheus (17/09):
 *
 *   · parente com o MESMO nome do fornecedor mostrado → só o lugar: "também em Mogi Guacu e Limeira"
 *     (as três SUPRITEC — repetir o nome inteiro ocupava 4 linhas no celular e não dizia nada novo);
 *   · parente com nome DIFERENTE → o nome inteiro: "parente de SATIS - SÃO PAULO".
 *   ❌ Nada de encurtar nome ("primeira palavra"): "GV SEG COMERCIO" viraria "GV" e a informação some.
 *
 * ⚠️ A cidade aparece como está no cadastro — em 17/09 gravada SEM ACENTO ("MOGI GUACU" → "Mogi Guacu"). É o dado, não o
 * código: pôr acento aqui seria inventar dado. Corrigir é no cadastro.
 */
import type { FornecedorParente } from '@/lib/compras-store';

/** "SUPRITEC TECNOLOGIA EM SEGURANCA LTDA — Campinas" (sem lugar, só o nome). */
export function nomeComLugar(nome: string, lugar: string | null | undefined): string {
  return lugar ? `${nome} — ${lugar}` : nome;
}

/** "Campinas", "Campinas e Limeira", "Campinas, Limeira e Mogi Guacu". */
function emLista(itens: string[]): string {
  if (itens.length <= 1) return itens.join('');
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

const mesmoNome = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * A frase do aviso de parente para o fornecedor `nome`:
 *   · só nomes diferentes → "parente de SATIS - SÃO PAULO"
 *   · só nomes iguais     → "também em Mogi Guacu e Limeira"
 *   · os dois             → "parente de X · também em Limeira"
 * Parente de nome igual SEM lugar (não acontece: parente sempre tem CNPJ válido) cai no nome inteiro — a tela não fica vazia.
 */
export function fraseDosParentes(nome: string, parentes: FornecedorParente[]): string {
  const iguais = parentes.filter((p) => mesmoNome(p.nome, nome) && p.lugar);
  const diferentes = parentes.filter((p) => !iguais.includes(p));
  const partes: string[] = [];
  if (diferentes.length) partes.push(`parente de ${diferentes.map((p) => p.nome).join(', ')}`);
  if (iguais.length) partes.push(`também em ${emLista(iguais.map((p) => p.lugar as string))}`);
  return partes.join(' · ');
}
